import { useState, useEffect } from "react";
import CustomAlertStore from "./CustomAlertStoreClass";

export default function CustomAlertModal() {
  const [message, setMessage] = useState(null);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    // Poll every 100ms to see if alert changed (simple & works)
    const interval = setInterval(() => {
      if (CustomAlertStore.alert !== alert) {
        setAlert(CustomAlertStore.alert);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [alert]);

  if (!alert) return null;

  return <Alert alert={alert} onClose={() => CustomAlertStore.clear()} title={alert.type} message={alert.message} type={alert.type} />;
}

const Alert = ({
  alert,
  onClose,
  title = "Alert",
  message = "This is an alert message",
  type = "info", // info, success, warning, error
}) => {
  if (!alert) return null;

  const getIconAndColor = () => {
    switch (type) {
      case "success":
        return {
          icon: (
            <svg className='storelense-alert-icon-svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7' />
            </svg>
          ),
          gradientFrom: "#10b981",
          gradientTo: "#14b8a6",
        };
      case "warning":
        return {
          icon: (
            <svg className='storelense-alert-icon-svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          ),
          gradientFrom: "#f59e0b",
          gradientTo: "#f97316",
        };
      case "error":
        return {
          icon: (
            <svg className='storelense-alert-icon-svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          ),
          gradientFrom: "#ef4444",
          gradientTo: "#f43f5e",
        };
      default: // info
        return {
          icon: (
            <svg className='storelense-alert-icon-svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
            </svg>
          ),
          gradientFrom: "#0ea5e9",
          gradientTo: "#06b6d4",
        };
    }
  };

  const { icon, gradientFrom, gradientTo } = getIconAndColor();

  return (
    <>
      <style jsx>{`
        .storelense-alert-overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99999;
          padding: 1rem;
          animation: storelense-alert-fade-in 0.2s ease-out;
        }

        @keyframes storelense-alert-fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .storelense-alert-container {
          background: white;
          border-radius: 1rem;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          max-width: 28rem;
          width: 100%;
          overflow: hidden;
          animation: storelense-alert-slide-up 0.3s ease-out;
          position: relative;
        }

        @keyframes storelense-alert-slide-up {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .storelense-alert-header {
          padding: 1.5rem;
          padding-bottom: 1rem;
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }

        .storelense-alert-icon-wrapper {
          flex-shrink: 0;
          width: 3rem;
          height: 3rem;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .storelense-alert-icon-svg {
          width: 1.75rem;
          height: 1.75rem;
          color: white;
        }

        .storelense-alert-content {
          flex: 1;
          min-width: 0;
        }

        .storelense-alert-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
          margin: 0;
          margin-bottom: 0.5rem;
          line-height: 1.4;
        }

        .storelense-alert-message {
          font-size: 0.95rem;
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
          word-wrap: break-word;
        }

        .storelense-alert-footer {
          padding: 1rem 1.5rem 1.5rem;
          display: flex;
          justify-content: flex-end;
        }

        .storelense-alert-button {
          padding: 0.625rem 2rem;
          background: linear-gradient(135deg, #0ea5e9, #06b6d4);
          background: hsl(221.2 83.2% 53.3% / 0.9);

          box-shadow: 0 4px 12px hsl(221.2 83.2% 53.3% / 0.3);
          color: white;
          border: none;
          border-radius: 0.5rem;
          font-weight: 600;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.3);
          min-width: 5rem;
        }

        .storelense-alert-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 12px -2px rgba(14, 165, 233, 0.4);
        }

        .storelense-alert-button:active {
          transform: translateY(0);
        }

        /* Responsive styles */
        @media (max-width: 640px) {
          .storelense-alert-overlay {
            padding: 0.75rem;
          }

          .storelense-alert-container {
            max-width: 100%;
          }

          .storelense-alert-header {
            padding: 1.25rem;
            padding-bottom: 0.75rem;
            gap: 0.875rem;
          }

          .storelense-alert-icon-wrapper {
            width: 2.5rem;
            height: 2.5rem;
            border-radius: 0.625rem;
          }

          .storelense-alert-icon-svg {
            width: 1.5rem;
            height: 1.5rem;
          }

          .storelense-alert-title {
            font-size: 1rem;
          }

          .storelense-alert-message {
            font-size: 0.94rem;
          }

          .storelense-alert-footer {
            padding: 0.75rem 1.25rem 1.25rem;
          }

          .storelense-alert-button {
            width: 100%;
            height: 3rem;
          }
        }

        @media (max-width: 380px) {
          .storelense-alert-header {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }

          .storelense-alert-content {
            width: 100%;
          }
        }
      `}</style>

      <div className='storelense-alert-overlay' onClick={onClose}>
        <div className='storelense-alert-container' onClick={(e) => e.stopPropagation()}>
          <div className='storelense-alert-header'>
            <div
              className='storelense-alert-icon-wrapper'
              style={{
                background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
              }}
            >
              {icon}
            </div>
            <div className='storelense-alert-content'>
              <h3 className='storelense-alert-title'>{title}</h3>
              <p className='storelense-alert-message'>{message}</p>
            </div>
          </div>
          <div className='storelense-alert-footer'>
            <button className='storelense-alert-button' onClick={onClose}>
              OK
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
